import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Collapse,
    TableFooter
} from '@mui/material';
import { seasonPlayersCollection } from '../services/players';
import { db } from '../firebase.js';
import { collection, getDocs, query, where, orderBy, doc, getDoc } from 'firebase/firestore';
import { useTheme } from '@mui/material/styles';
import { page } from '../theme/commonStyles';
import { useNotification } from '../context/NotificationContext';
import PlayerCard from './PlayerCard';
import { useSidelinedMap } from '../hooks/useSidelinedMap';

function ParticipantTeamModal({ open, onClose, auctionId, participant, roundId, seasonId, roundStatus, matchId }) {
    const theme = useTheme();
    const notify = useNotification();
    const [loading, setLoading] = useState(true);
    const [teamPlayers, setTeamPlayers] = useState([]);
    const [playerRoundScores, setPlayerRoundScores] = useState({});
    const [scoringRules, setScoringRules] = useState({});
    const [expandedPlayerId, setExpandedPlayerId] = useState(null);
    const [captainId, setCaptainId] = useState(null);
    const [tacticPoints, setTacticPoints] = useState(null);
    const [opponentMap, setOpponentMap] = useState(new Map());
    const sidelinedMap = useSidelinedMap(seasonId);

    useEffect(() => {
        const fetchScoringRules = async () => {
            try {
                const rulesCollection = collection(db, 'scoringRules');
                const rulesSnapshot = await getDocs(rulesCollection);
                const rulesData = rulesSnapshot.docs.reduce((acc, doc) => {
                    acc[doc.id] = doc.data();
                    return acc;
                }, {});
                setScoringRules(rulesData);
            } catch (err) {
                console.error("Error fetching scoring rules:", err);
            }
        };
        fetchScoringRules();
    }, []);

    useEffect(() => {
        const fetchTeamData = async () => {
            if (!open || !participant || !auctionId || !seasonId) {
                return;
            }


            setLoading(true);
            setTeamPlayers([]);
            setPlayerRoundScores({});
            setExpandedPlayerId(null);
            setCaptainId(null);
            setTacticPoints(null);
            setOpponentMap(new Map());

            try {
                let playerIds = [];

                if (roundStatus === 'closed' && roundId) {
                    const playersPerRoundQuery = query(
                        collection(db, 'auctions', auctionId, 'playersPerParticipantPerRound'),
                        where('roundId', '==', roundId),
                        where('participantId', '==', participant.id)
                    );
                    const snapshot = await getDocs(playersPerRoundQuery);
                    playerIds = snapshot.docs.map(doc => doc.data().playerId);
                } else {
                    const transfersQuery = query(collection(db, 'auctions', auctionId, 'auctionTransfers'), orderBy('timestamp', 'asc'));
                    const transfersSnap = await getDocs(transfersQuery);
                    const myPlayersMap = new Map();
                    transfersSnap.docs.forEach(doc => {
                        const transfer = doc.data();
                        if (transfer.to === participant.id) {
                            myPlayersMap.set(transfer.playerId, { id: transfer.playerId, name: transfer.playerName });
                        }
                        if (transfer.from === participant.id && transfer.to !== participant.id) {
                            myPlayersMap.delete(transfer.playerId);
                        }
                    });
                    playerIds = Array.from(myPlayersMap.keys());
                }

                if (playerIds.length === 0) {
                    setTeamPlayers([]);
                    setLoading(false);
                    return;
                }

                const playerIdsAsNumbers = playerIds.map(id => Number(id));
                const playersQuery = query(seasonPlayersCollection(seasonId), where('id', 'in', playerIdsAsNumbers));
                const playersSnap = await getDocs(playersQuery);
                const fetchedPlayers = playersSnap.docs.map(doc => doc.data());

                const positionMap = {
                    'Goalkeeper': 'Keeper',
                    'Defender': 'Verdediger',
                    'Midfielder': 'Middenvelder',
                    'Attacker': 'Aanvaller',
                    'Coach': 'Coach'
                };

                const translatedPlayers = fetchedPlayers.map(p => ({ ...p, position: positionMap[p.position] || p.position }));
                setTeamPlayers(translatedPlayers);

                if (roundId && seasonId) {
                    const scoresCollectionRef = collection(db, `playerScores/${seasonId}/rounds/${roundId}/scores`);
                    const scoresSnapshot = await getDocs(scoresCollectionRef);

                    const fetchedScores = {};
                    scoresSnapshot.forEach(doc => {
                        const scoreData = doc.data();
                        const playerId = String(scoreData.playerId);
                        if (!fetchedScores[playerId]) {
                            fetchedScores[playerId] = [];
                        }
                        fetchedScores[playerId].push(scoreData);
                    });
                    setPlayerRoundScores(fetchedScores);
                }

                // Fetch the real matches for this round to find opponents for each club
                if (roundId && seasonId) {
                    const matchesQuery = query(collection(db, `schedule/${seasonId}/rounds/${roundId}/matches`));
                    const matchesSnapshot = await getDocs(matchesQuery);
                    const newOpponentMap = new Map();
                    matchesSnapshot.forEach(doc => {
                        const match = doc.data();
                        if (match.homeTeamName) {
                            newOpponentMap.set(match.homeTeamName, {
                                opponentName: match.awayTeamName,
                                location: 'Thuis'
                            });
                        }
                        if (match.awayTeamName) {
                            newOpponentMap.set(match.awayTeamName, {
                                opponentName: match.homeTeamName,
                                location: 'Uit'
                            });
                        }
                    });
                    setOpponentMap(newOpponentMap);
                }

                // Fetch captain information using the direct matchId
                if (matchId && participant?.id) {
                    try {
                        const matchSelectionsRef = doc(db, 'auctions', auctionId, 'matchSelections', participant.id);
                        const matchSelectionsSnap = await getDoc(matchSelectionsRef);

                        if (matchSelectionsSnap.exists()) {
                            const selections = matchSelectionsSnap.data().selections || {};
                            const selection = selections[matchId];
                            if (selection && selection.captain) {
                                setCaptainId(String(selection.captain));
                            }
                        }
                    } catch (err) {
                        console.error('Error fetching captain info:', err);
                    }
                }

                // Fetch tactic points if round is closed
                if (roundStatus === 'closed' && roundId) {
                    try {
                        // Extract round key from roundId (e.g., from sportmonks round ID to round1, round2, etc.)
                        const scheduleQuery = query(collection(db, 'auctions', auctionId, 'schedule'));
                        const scheduleSnap = await getDocs(scheduleQuery);
                        let scheduleRoundId = null;

                        scheduleSnap.docs.forEach(doc => {
                            const scheduleData = doc.data();
                            if (scheduleData.roundId === roundId) {
                                scheduleRoundId = doc.id;
                            }
                        });

                        if (scheduleRoundId) {
                            const roundRankingRef = doc(db, 'auctions', auctionId, 'roundRankings', scheduleRoundId);
                            const roundRankingSnap = await getDoc(roundRankingRef);

                            if (roundRankingSnap.exists()) {
                                const roundData = roundRankingSnap.data();
                                const participantResult = roundData.results?.find(r => r.uid === participant.id);
                                if (participantResult) {
                                    setTacticPoints({
                                        tacticPoints: participantResult.tacticPoints || 0,
                                        tacticCounter: participantResult.tacticCounter || 0
                                    });
                                }
                            }
                        }
                    } catch (err) {
                        console.error('Error fetching tactic points:', err);
                    }
                }

            } catch (err) {
                console.error("❌ Fout bij het ophalen van teamdata:", err);
                console.error("❌ Error details:", {
                    message: err.message,
                    code: err.code,
                    stack: err.stack
                });
                notify.error('Kon de teamgegevens niet laden.');
            } finally {
                setLoading(false);
            }
        };

        fetchTeamData();
    }, [open, auctionId, participant, roundId, seasonId, roundStatus, matchId, notify]);

    const handlePlayerRowClick = (playerId) => {
        setExpandedPlayerId(prev => (prev === playerId ? null : playerId));
    };

    const getPlayerScoreForRound = (player) => {
        const playerIdStr = String(player.id);

        // Als er geen scores zijn voor deze speler in deze ronde, hebben ze nog niet gespeeld.
        if (!playerRoundScores.hasOwnProperty(playerIdStr)) {
            return null;
        }
        const scoresArray = playerRoundScores[playerIdStr] || [];
        const isCaptain = captainId === playerIdStr;

        let totalPoints = 0;
        scoresArray.forEach(score => {
            if (isCaptain) {
                const bonusPointsToExclude = [
                    "defender_bonus",
                    "spielmacher_bonus",
                    "save_bonus",
                    "interceptions_bonus",
                ];
                let bonusPoints = 0;
                let basePoints = score.totalPoints || 0;
                const breakdown = score.breakdown || {};

                for (const bonus of bonusPointsToExclude) {
                    if (breakdown[bonus]) {
                        bonusPoints += breakdown[bonus].points;
                    }
                }
                basePoints -= bonusPoints;
                totalPoints += (basePoints * 1.5) + bonusPoints;
            } else {
                totalPoints += score.totalPoints || 0;
            }
        });
        return totalPoints;
    };

    const renderScoreBreakdown = (score, isCaptain = false) => {
        if (!score || !score.breakdown) return null;

        const sortedBreakdown = Object.entries(score.breakdown).sort(([ruleA], [ruleB]) => {
            const orderA = scoringRules[ruleA]?.order || 999;
            const orderB = scoringRules[ruleB]?.order || 999;
            return orderA - orderB;
        });

        const bonusPointsToExclude = [
            "defender_bonus",
            "spielmacher_bonus",
            "save_bonus",
            "interceptions_bonus",
        ];

        let bonusPoints = 0;
        const breakdown = score.breakdown || {};
        for (const bonus of bonusPointsToExclude) {
            if (breakdown[bonus]) {
                bonusPoints += breakdown[bonus].points;
            }
        }

        const totalPoints = score.totalPoints || 0;
        const basePoints = totalPoints - bonusPoints;
        const finalPoints = isCaptain ? (basePoints * 1.5) + bonusPoints : totalPoints;

        return (
            <Box>
                <Typography variant="subtitle2" gutterBottom>{score.fixtureName}</Typography>
                <Table size="small" aria-label="score breakdown">
                    <TableHead>
                        <TableRow>
                            <TableCell>Actie</TableCell>
                            <TableCell align="right">Aantal</TableCell>
                            <TableCell align="right">Punten</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedBreakdown.map(([rule, data]) => (
                            <TableRow key={rule}>
                                <TableCell>{scoringRules[rule]?.action || rule}</TableCell>
                                <TableCell align="right">{data.count}</TableCell>
                                <TableCell align="right">{data.points}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                    <TableFooter>
                        {isCaptain ? (
                            <>
                                <TableRow>
                                    <TableCell colSpan={2} sx={{ borderBottom: 'none', pt: 2, pb: 0 }}>Basispunten</TableCell>
                                    <TableCell align="right" sx={{ borderBottom: 'none', pt: 2, pb: 0 }}>{basePoints}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell colSpan={2} sx={{ pt: 0, pb: 0 }}>Aanvoerder bonus</TableCell>
                                    <TableCell align="right" sx={{ pt: 0, pb: 0 }}>x 1.5</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell colSpan={2} sx={{ pt: 0, pb: 1 }}>Subtotaal</TableCell>
                                    <TableCell align="right" sx={{ pt: 0, pb: 1 }}>{basePoints * 1.5}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell colSpan={2} sx={{ pt: 0, pb: 1 }}>Bonuspunten</TableCell>
                                    <TableCell align="right" sx={{ pt: 0, pb: 1 }}>+ {bonusPoints}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell colSpan={2} sx={{ fontWeight: 'bold', borderTop: '1px solid rgba(81, 81, 81, 1)' }}>Eindtotaal</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main', borderTop: '1px solid rgba(81, 81, 81, 1)' }}>{finalPoints}</TableCell>
                                </TableRow>
                            </>
                        ) : (
                            <TableRow>
                                <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>Totaal</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{totalPoints}</TableCell>
                            </TableRow>
                        )}
                    </TableFooter>
                </Table>
            </Box>
        );
    };

    const getRowStyle = (item) => {
        const position = item.position;
        return theme.positions[position]?.backgroundColor || 'transparent';
    };

    const rosterLines = {
        'Coach': { max: 1, players: [] },
        'Keeper': { max: 1, players: [] },
        'Verdediger': { max: 5, players: [] },
        'Middenvelder': { max: 5, players: [] },
        'Aanvaller': { max: 4, players: [] },
    };

    teamPlayers.forEach(player => {
        if (rosterLines[player.position]) {
            rosterLines[player.position].players.push(player);
        }
    });

    const rosterToRender = [];
    Object.values(rosterLines).forEach(line => {
        rosterToRender.push(...line.players);
    });

    const hasScores = roundId && seasonId && Object.keys(playerRoundScores).length > 0;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            sx={{
                '& .MuiDialog-paper': {
                    width: { xs: 'calc(100% - 48px)' },
                    margin: { xs: '24px' }
                }
            }}
        >
            <DialogTitle>Team van {participant?.name}</DialogTitle>
            <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
                {loading && (<Box sx={page.loadingContainer}><CircularProgress /></Box>)}
                {!loading && (
                    <>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                            {rosterToRender.map((player) => {
                                const playerScoresArray = playerRoundScores[String(player.id)] || [];
                                const isCaptain = captainId === String(player.id);
                                const displayPoints = getPlayerScoreForRound(player);
                                const isExpanded = expandedPlayerId === player.id;
                                const opponent = opponentMap.get(player.clubName);
                                const showCaptainMark = isCaptain && (roundStatus === 'running' || roundStatus === 'closed');

                                return (
                                    <PlayerCard
                                        key={player.id}
                                        player={player}
                                        isCaptain={showCaptainMark}
                                        status={sidelinedMap.get(String(player.id)) || null}
                                        bgcolor={getRowStyle(player)}
                                        onClick={() => handlePlayerRowClick(player.id)}
                                        subCaption={opponent && (
                                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '0.6rem', display: 'block', lineHeight: 1.4 }}>
                                                {opponent.opponentName} ({opponent.location})
                                            </Typography>
                                        )}
                                        rightSlot={hasScores && displayPoints !== null ? (
                                            <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: isCaptain ? 'bold' : 'normal', color: isCaptain ? 'primary.main' : 'inherit', minWidth: '2ch', textAlign: 'right' }}>
                                                {displayPoints}
                                            </Typography>
                                        ) : null}
                                        footer={
                                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                <Box sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.2)' }}>
                                                    {isExpanded && playerScoresArray.length > 0 ? (
                                                        playerScoresArray.map((score, index) => (
                                                            <Box key={index} sx={{ mb: index < playerScoresArray.length - 1 ? 2 : 0 }}>
                                                                {renderScoreBreakdown(score, isCaptain)}
                                                            </Box>
                                                        ))
                                                    ) : (
                                                        isExpanded && <Typography variant="body2" sx={{ textAlign: 'center', p: 2 }}>Geen puntendetails beschikbaar voor deze speler.</Typography>
                                                    )}
                                                </Box>
                                            </Collapse>
                                        }
                                    />
                                );
                            })}
                        </Box>

                        {/* Total Points Summary */}
                        {hasScores && (
                            <Box sx={{ p: 2, mt: 2, border: '1px solid', borderColor: 'rgba(255, 255, 255, 0.08)', borderRadius: 1 }}>
                                <Typography variant="h6" gutterBottom>Totaal Punten</Typography>
                                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'space-around' }}>
                                    <Box textAlign="center">
                                        <Typography variant="body2" color="text.secondary">Spelers</Typography>
                                        <Typography variant="h6">
                                            {(() => {
                                                const totalPlayerPoints = teamPlayers.reduce((sum, player) => {
                                                    if (player.isPlaceholder) return sum;
                                                    return sum + getPlayerScoreForRound(player);
                                                }, 0);
                                                return totalPlayerPoints;
                                            })()}
                                        </Typography>
                                    </Box>
                                    {tacticPoints && (
                                        <>
                                            <Box textAlign="center">
                                                <Typography variant="body2" color="text.secondary">Tactiek</Typography>
                                                <Typography variant="h6" color="primary.main">
                                                    {tacticPoints.tacticPoints}
                                                </Typography>
                                            </Box>
                                            <Box textAlign="center">
                                                <Typography variant="body2" color="text.secondary">Kraken</Typography>
                                                <Typography variant="h6" color="secondary.main">
                                                    {tacticPoints.tacticCounter}
                                                </Typography>
                                            </Box>
                                        </>
                                    )}
                                    <Box textAlign="center">
                                        <Typography variant="body2" color="text.secondary">Totaal</Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                            {(() => {
                                                const totalPlayerPoints = teamPlayers.reduce((sum, player) => {
                                                    if (player.isPlaceholder) return sum;
                                                    return sum + getPlayerScoreForRound(player);
                                                }, 0);
                                                const tacticTotal = tacticPoints ? (tacticPoints.tacticPoints + tacticPoints.tacticCounter) : 0;
                                                return totalPlayerPoints + tacticTotal;
                                            })()} punten
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Sluiten</Button>
            </DialogActions>
        </Dialog>
    );
}

export default ParticipantTeamModal;
